@if (mode === 'admin') {
  <div class="transcription-actions-bar">
    <button
      class="me-2"
      type="button"
      mat-raised-button
      color="primary"
      aria-label="Open tools button"
      matTooltip="{{ 'TRANSCRIPTIONS.TOOLS' | translate }}"
      matTooltipPosition="below"
      [disabled]="apiLoadingTools"
      (click)="openTools()">
      @if (apiLoadingTools) {
        <mat-spinner class="m-0" diameter="20"></mat-spinner>
      } @else {
        <mat-icon class="m-0">construction</mat-icon>
      }
    </button>

    <button
      class="me-2"
      type="button"
      mat-raised-button
      [color]="isRecording ? 'warn' : 'primary'"
      aria-label="Record audio button"
      [matTooltip]="isRecording ? ('TRANSCRIPTIONS.STOP_RECORDING' | translate) + ' (' + recordingDuration + ')' : ('TRANSCRIPTIONS.RECORD_AUDIO' | translate)"
      matTooltipPosition="below"
      [disabled]="isProcessingRecording"
      (click)="toggleRecording()">
      @if(isProcessingRecording) {
        <mat-spinner class="m-0" diameter="20"></mat-spinner>
      } @else {
        <mat-icon class="m-0">{{ isRecording ? 'voice_over_off' : 'record_voice_over' }}</mat-icon>
      }
    </button>

    <button
      type="button"
      mat-raised-button
      color="primary"
      aria-label="Create transcription button"
      matTooltip="{{ 'TRANSCRIPTIONS.TRANSCRIBE' | translate }}"
      matTooltipPosition="below"
      (click)="openCreateTranscriptionDialog()">
      <mat-icon class="m-0">upload</mat-icon>
    </button>
  </div>
}

<div class="transcription-chat-form" [class.has-video]="isVideoFile(transcription.fileUrl)" [class.transcription-chat-form--client]="mode === 'client'" *ngIf="isFormVisible && transcription" #formContainer>
  <div class="form-header">
    <div class="filename-section">
      @if (mode === 'admin' && editingFileName) {
        <div class="filename-edit">
          <mat-icon>description</mat-icon>
          <input
            type="text"
            class="filename-input"
            [value]="transcription.fileName"
            (input)="onFileNameInput($event)"
            (blur)="onFileNameBlur($event)"
            (keydown.enter)="onEnterKey($event)"
            (keydown.escape)="onEscapeKey($event)"
            #fileNameInput
          />
        </div>
      } @else {
        <h3 class="filename-display">
          <!-- Title is read-only for clients: no click-to-edit, no edit hint. -->
          <div class="filename-clickable-area" (click)="mode === 'admin' && startEditingFileName()">
            <mat-icon class="file-icon">description</mat-icon>
            <span class="filename-text">{{ transcription.fileName || 'Untitled Transcription' }}</span>
            @if (mode === 'admin') {
              <mat-icon class="edit-hint">edit</mat-icon>
            }
          </div>
        </h3>
      }
      @if (mode === 'admin' && transcriptionSourceUrl) {
        <a class="source-url-link" [href]="transcriptionSourceUrl" target="_blank" rel="noopener">
          <mat-icon class="source-url-icon">link</mat-icon>
          <span>{{ transcriptionSourceUrl }}</span>
        </a>
      }
    </div>

    <div class="form-actions">
      <!-- Editing controls (find & replace, add) are admin-only; clients get a read-only view. -->
      @if (mode === 'admin') {
      <button
        type="button"
        mat-icon-button
        color="primary"
        [disabled]="!hasTranscriptionContent"
        (click)="openFindReplaceDialog()"
        [matTooltip]="'TRANSCRIPTIONS.FIND_AND_REPLACE' | translate">
        <mat-icon>find_replace</mat-icon>
      </button>
      <button
        type="button"
        mat-icon-button
        color="primary"
        (click)="addContentItem()"
        [matTooltip]="'BUTTON.ADD' | translate">
        <mat-icon>add</mat-icon>
      </button>
      }
      <button
        type="button"
        mat-icon-button
        color="warn"
        (click)="clearForm()"
        [matTooltip]="'BUTTON.CLOSE' | translate">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Video Timeline -->
  @if (transcription && transcription.content && transcription.content.length > 0) {
    @if (transcription.type === 'YOUTUBE' && !transcription.fileUrl && transcriptionSourceUrl) {
      <div class="youtube-video-removed">
        <p class="video-removed-message">{{ 'TRANSCRIPTIONS.VIDEO_REMOVED' | translate }}</p>
        <a class="watch-on-youtube-button" mat-stroked-button color="primary" [href]="transcriptionSourceUrl" target="_blank" rel="noopener">
          <mat-icon>smart_display</mat-icon>
          {{ 'TRANSCRIPTIONS.WATCH_ON_YOUTUBE' | translate }}
        </a>
      </div>
    }
    <app-video-timeline
      [class.video-timeline--client]="mode === 'client'"
      [transcriptionContent]="transcription.content"
      [totalDuration]="totalDuration"
      [transcription]="transcription"
      [externalCurrentTime]="timelineCurrentTime"
      [isVideoFile]="isVideoFile(transcription.fileUrl)"
      [isVideoVisible]="isVideoVisible"
      [captionsEnabled]="captionsEnabled"
      [mergedTextEnabled]="isTextMerged"
      (timeChanged)="onTimelineTimeChanged($event)"
      (transcriptionSegmentClicked)="onTranscriptionSegmentClicked($event)"
      (durationChanged)="onTimelineDurationChanged($event)"
      (videoVisibilityToggled)="onVideoVisibilityToggled($event)"
      (captionsToggled)="onCaptionsToggled($event)"
      (mergedTextToggled)="onMergedTextToggled($event)"
      #videoTimeline>
    </app-video-timeline>
  }

  <div class="chat-container" #chatContainer>
    @if (transcription && transcription.content && transcription.content.length > 0) {
      @if (isTextMerged) {
      <div class="chat-messages merged-view">
        <p class="merged-text-display">{{ mergedText }}</p>
      </div>
      } @else {
      <div class="chat-messages">
        @for (message of transcription.content; track message.id || message; let i = $index) {
          <div class="chat-message">
            <!-- Time display -->
            <div class="message-time">
              @if (editingField?.contentIndex === i && editingField?.field === 'startTime') {
                <input
                  type="text"
                  class="time-input"
                  [value]="formatTime(message.startTime)"
                  (input)="onTimeInput(i, 'startTime', $event)"
                  (blur)="onFieldBlur(i, 'startTime', $event)"
                  (keydown.enter)="onEnterKey($event)"
                  (keydown.escape)="onEscapeKey($event)"
                  (keydown)="onTimeKeydown($event)"
                  placeholder="MM:SS:MMM"
                  #startTimeInput
                />
              } @else {
                <span class="time-display" (click)="mode === 'admin' && onTimeDisplayClick(i, 'startTime', message.startTime)">
                  {{ formatTime(message.startTime) }}
                </span>
              }

              <span class="time-separator">-</span>

              @if (editingField?.contentIndex === i && editingField?.field === 'endTime') {
                <input
                  type="text"
                  class="time-input"
                  [value]="formatTime(message.endTime)"
                  (input)="onTimeInput(i, 'endTime', $event)"
                  (blur)="onFieldBlur(i, 'endTime', $event)"
                  (keydown.enter)="onEnterKey($event)"
                  (keydown.escape)="onEscapeKey($event)"
                  (keydown)="onTimeKeydown($event)"
                  placeholder="MM:SS:MMM"
                  #endTimeInput
                />
              } @else {
                <span class="time-display" (click)="mode === 'admin' && onTimeDisplayClick(i, 'endTime', message.endTime)">
                  {{ formatTime(message.endTime) }}
                </span>
              }

              <!-- Tiny jump button -->
              <button
                type="button"
                mat-icon-button
                class="tiny-jump-btn"
                (click)="jumpToSegment(message.startTime)"
                [matTooltip]="'VIDEO_TIMELINE.JUMP' | translate">
                <mat-icon>redo</mat-icon>
              </button>
            </div>

            <!-- Message bubble -->
            <div
              class="message-bubble"
              [class.message-bubble-playing]="i === currentSegmentIndex"
              [class.clickable-segment]="mode === 'client'"
              (click)="mode === 'client' && jumpToSegment(message.startTime)">
              <!-- Message text -->
              <div class="message-text">
                @if (editingField?.contentIndex === i && editingField?.field === 'text') {
                  <p
                    class="text-display editing"
                    contentEditable="true"
                    spellcheck="false"
                    (input)="onFieldInput(i, 'text', $event)"
                    (blur)="onFieldBlur(i, 'text', $event)"
                    (keydown.enter)="onEnterKey($event)"
                    (keydown.escape)="onEscapeKey($event)"
                    #textInput>
                  </p>
                } @else {
                  <p
                    class="text-display"
                    (click)="mode === 'admin' && startEditing(i, 'text')">
                    {{ message.text || (mode === 'admin' ? 'Click to edit message...' : '') }}
                  </p>
                }
              </div>

              <!-- Remove button (admin-only; clients are read-only) -->
              @if (mode === 'admin') {
              <button
                type="button"
                mat-icon-button
                color="warn"
                class="remove-message-btn"
                (click)="removeContentItem(i)"
                [matTooltip]="'BUTTON.DELETE' | translate">
                <mat-icon>delete</mat-icon>
              </button>
              }
            </div>
          </div>
        }
      </div>
      }
    } @else if (transcription) {
      <div class="empty-chat">
        <mat-icon class="empty-icon">pending</mat-icon>
        <p>{{ 'TRANSCRIPTIONS.NO_TRANSCRIPTION' | translate }}</p>
      </div>
    } @else {
      <div class="empty-chat">
        <mat-icon class="empty-icon">description</mat-icon>
      </div>
    }
  </div>
</div>
